import { AfterViewInit, Component, Input, OnInit, ViewChildren } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Observable, forkJoin, of } from 'rxjs';
import { I18nTranslator } from '../../../../i18n/translate';
import {
  ARConsoleFormatType,
  ARConsoleModulationType,
  ARConsoleType,
  ARConsoleVersion,
  ARFertilizerType,
} from '../../../../models/agrorendement-common-lists.model';
import { GCFarm, GCOrderItemConsole, GCParcel } from '../../../../models/agrorendement-genconsole.model';
import { ARCommonListType, ARCommonListsService } from '../../../../services/agrorendement-common-lists.service';
import { ModalService } from '../../../../services/modal.service';
import { ARGenConsoleService } from '../../../../services/agrorendement-genconsole.service';
import { sortingNumber, sortingString, toNumber } from '../../../../utils/utils';
import { ARVraEditComponent } from '../common/vra-edit.component';
import {
  GenconsoleSelectConsoleDialogComponent,
  GenconsoleSelectConsoleDialogContext,
} from './genconsole.selectconsole-dialog.component';
import { isGenConsoleFormatSupported, isGenConsoleModelSupported, isGenConsoleVersionSupported } from './utils';
import { TranslatePipe } from '../../../../pipes/translate.pipe';

@Component({
  standalone: true,
  selector: 'ar-genconsole-selectconsole-cmp',
  templateUrl: './genconsole.selectconsole.component.html',
  imports: [RouterLink, RouterLinkActive, RouterOutlet, CommonModule, ARVraEditComponent, TranslatePipe],
  providers: [ARGenConsoleService, ARCommonListsService],
})
export class ARGenConsoleSelectConsoleComponent implements OnInit, AfterViewInit {
  @ViewChildren('parcelCheckbox') parcelCheckboxes: any;

  @Input()
  culture: string;

  @Input()
  farms: Array<GCFarm>;

  @Input()
  parcels: Array<GCParcel>;

  checkedParcelIds: Set<number> = new Set<number>();
  farmParcelIndex = new Map<number, Array<GCParcel>>();

  @Input()
  consoles: Array<GCOrderItemConsole>;

  consoleModulationTypes: ARConsoleModulationType[];
  consoleFormatTypes: ARConsoleFormatType[];
  consoleTypes: ARConsoleType[];
  consoleVersions: ARConsoleVersion[];
  fertilizerTypes: ARFertilizerType[];

  consoleModulationTypesIndex = new Map<number, ARConsoleModulationType>();
  consoleFormatTypesIndex = new Map<number, ARConsoleFormatType>();
  consoleTypesIndex = new Map<number, ARConsoleType>();
  consoleVersionsIndex = new Map<number, ARConsoleVersion>();
  fertilizerTypeIndex = new Map<number, ARFertilizerType>();

  areListsFetched = false;

  constructor(
    private genconsoleService: ARGenConsoleService,
    private commonListService: ARCommonListsService,
    private i18nTranslator: I18nTranslator,
    private modal: ModalService,
    private router: Router,
  ) {}

  getConsoles() {
    const orderItemIds = Array<number>();
    for (const parcel of this.parcels) {
      orderItemIds.push(parcel.orderItemId);
    }
    return this.genconsoleService.getConsolesForParcels(this.culture, orderItemIds);
  }

  handleConsoles(consoles: GCOrderItemConsole[]) {
    const parcelIndex = new Map<number, GCParcel>();
    for (const parcel of this.parcels) {
      parcelIndex.set(parcel.orderItemId, parcel);
    }

    for (const console of consoles) {
      const parcel = parcelIndex.get(console.orderItemId);
      parcel.console = console;
      console.name = parcel.name;
      console.parcelId = parcel.id;
    }

    // pour que la ref de this.consoles reste identique
    this.consoles.push(...consoles);
  }

  getLists() {
    const listTypes = [
      ARCommonListType.CONSOLE_FORMAT_TYPES,
      ARCommonListType.CONSOLE_TYPES,
      ARCommonListType.CONSOLE_VERSIONS,
      ARCommonListType.CONSOLE_MODULATION_TYPES,
      ARCommonListType.FERTILIZER_TYPES,
    ];
    const obs1 = this.commonListService.getListTypes(ARCommonListType, ...listTypes);
    // pour garder le meme type de retour que pour dessous
    return obs1;
  }

  handleLists(aLists: any) {
    this.consoleModulationTypes = aLists.get(ARCommonListType.CONSOLE_MODULATION_TYPES).sort(sortingNumber('id'));
    this.consoleFormatTypes = aLists.get(ARCommonListType.CONSOLE_FORMAT_TYPES).sort(sortingString('label'));
    this.consoleTypes = aLists.get(ARCommonListType.CONSOLE_TYPES).sort(sortingString('label'));
    this.consoleVersions = aLists.get(ARCommonListType.CONSOLE_VERSIONS).sort(sortingString('label'));
    this.fertilizerTypes = aLists.get(ARCommonListType.FERTILIZER_TYPES).sort(sortingString('label'));

    for (const p of this.consoleModulationTypes) {
      this.consoleModulationTypesIndex.set(p.id, p);
    }
    for (const p of this.consoleFormatTypes) {
      this.consoleFormatTypesIndex.set(p.id, p);
    }
    for (const p of this.consoleTypes) {
      this.consoleTypesIndex.set(p.id, p);
    }
    for (const p of this.consoleVersions) {
      this.consoleVersionsIndex.set(p.id, p);
    }
    for (const p of this.fertilizerTypes) {
      this.fertilizerTypeIndex.set(p.id, p);
    }

    this.areListsFetched = true;
  }

  ngOnInit() {
    this.consoles.splice(0, this.consoles.length);

    const obs1 = this.getConsoles();
    let obs2: any = null;
    if (this.areListsFetched) {
      obs2 = of(null);
    } else {
      obs2 = this.getLists();
    }
    forkJoin([obs1, obs2]).subscribe(
      (data: Array<any>) => {
        const consoles: GCOrderItemConsole[] = data[0];
        this.handleConsoles(consoles);

        if (!this.areListsFetched) {
          const lists = data[1];
          this.handleLists(lists);
        }
      },
      (e) => {
        this.handleError(e);
      },
    );

    for (let i = 0; i < this.farms.length; i++) {
      this.farmParcelIndex.set(this.farms[i].id, this.farms[i].parcels);
    }
  }
  handleError(e: any) {
    this.modal.alert(this.i18nTranslator.translate('ar.gc.sc.error.consoles'));
  }

  ngAfterViewInit() {}
  isParcelChecked(parcel: GCParcel): boolean {
    return this.checkedParcelIds.has(parcel.id);
  }

  isFormatUnsupported(console: GCOrderItemConsole) {
    if (!console) {
      return false;
    }
    const format = this.consoleFormatTypesIndex.get(toNumber(console.consoleFormatType.id));
    return !isGenConsoleFormatSupported(format);
  }

  isConsoleUnsupported(console: GCOrderItemConsole) {
    if (!console) {
      return false;
    }
    const consoleType = this.consoleTypesIndex.get(toNumber(console.consoleType.id));
    return !isGenConsoleModelSupported(consoleType);
  }

  isVersionUnsupported(console: GCOrderItemConsole) {
    if (!console) {
      return false;
    }
    const consoleVersion = this.consoleVersionsIndex.get(toNumber(console.consoleVersion.id));
    return !isGenConsoleVersionSupported(consoleVersion);
  }

  showFormatType(console: GCOrderItemConsole) {
    if (!console) {
      return '';
    }

    const format = this.consoleFormatTypesIndex.get(toNumber(console.consoleFormatType.id));
    if (format && format.label) {
      return format.label;
    }

    if (format && format.code == 'UNKNOWN') {
      return this.i18nTranslator.translate('ar.gc.sc.unknown');
    }
    return '';
  }

  showConsoleType(console: GCOrderItemConsole) {
    if (!console) {
      return '';
    }

    const consoleType = this.consoleTypesIndex.get(toNumber(console.consoleType.id));
    if (consoleType && consoleType.label) {
      return consoleType.label;
    }

    if (consoleType && consoleType.code === 'OTHER') {
      return this.i18nTranslator.translate('ar.gc.sc.other.marque');
    }

    return '';
  }

  showVersionType(console: GCOrderItemConsole) {
    if (!console) {
      return '';
    }

    const version = this.consoleVersionsIndex.get(toNumber(console.consoleVersion.id));
    if (version && version.label) {
      return version.label;
    }

    if (version && version.code === 'OTHER') {
      return this.i18nTranslator.translate('ar.gc.sc.other.version');
    }

    return '';
  }

  showFertilizerType(console: GCOrderItemConsole) {
    if (!console) {
      return '';
    }

    if (!console.fertilizerType) {
      return '';
    }

    const fertilizer = this.fertilizerTypeIndex.get(toNumber(console.fertilizerType.id));
    if (fertilizer && fertilizer.label) {
      return fertilizer.label;
    }

    return '';
  }

  editConsole(console: GCOrderItemConsole) {
    if (!console) {
      return;
    }

    const context = new GenconsoleSelectConsoleDialogContext();
    context.consoles = [];
    context.consoles.push(console);

    this.modal.open(GenconsoleSelectConsoleDialogComponent, context);
  }

  onCheckboxClicked(parcelnumber: number, indexCheck: number, parcel: GCParcel) {
    let index = indexCheck;
    for (let i = 0; i < parcelnumber; i++) {
      index += this.farmParcelIndex.get(this.farms[i].id).length;
    }
    const checkboxes = this.parcelCheckboxes.toArray().map((x: any) => x.nativeElement);
    const checkbox = checkboxes[index];
    if (checkbox.checked) {
      this.checkedParcelIds.add(parcel.id);
    } else {
      this.checkedParcelIds.delete(parcel.id);
    }
  }

  editConsoles(parcelId: Set<number>) {
    const consoles: Array<GCOrderItemConsole> = [];
    parcelId.forEach((x: number) => {
      const found = this.parcels.find((element) => element.id == x);
      if (found != undefined) {
        consoles.push(found.console);
      }
    });
    if (consoles.length == 0) {
      return;
    }
    const context = new GenconsoleSelectConsoleDialogContext();
    context.consoles = [].concat(consoles);
    this.modal.open(GenconsoleSelectConsoleDialogComponent, context);
  }
}
